<script context="module">
  import { ORIGIN, COUNTRY_ID } from '../constants';
  import { createRequest } from '../utils/request';
  import axios from 'axios';

  export async function load({ session }) {
    const { API_BASE_URL, API_KEY, LANGUAGE } = session;

    const request = createRequest({
      apiBaseUrl: API_BASE_URL,
      apiKey: API_KEY,
      language: LANGUAGE,
    });

    const [categories, countries] = await Promise.all([
      request({ url: '/categories', method: 'GET' }),
      request({ url: '/countries', method: 'GET' }),
    ]);

    return {
      props: {
        categories:
          categories && categories.data && categories.data.Results
            ? categories.data.Results
            : [],
        countries: countries.data,
        API_BASE_URL,
        API_KEY,
        LANGUAGE,
      },
    };
  }
</script>

<script>
  import slugify from 'slugify';
  import qs from 'qs';
  import throttle from 'lodash/throttle';
  import debounce from 'lodash/debounce';
  import { FinnishBusinessIds } from 'finnish-business-ids';

  import { onMount } from 'svelte';

  import SelectPricePlans from '../components/Voss/SelectPricePlans.svelte';
  import Alert from '../components/Alert.svelte';
  import Field from '../components/Field.svelte';
  import Button from '../components/Button.svelte';
  import Stepper from '../components/Stepper.svelte';

  import { _ } from 'svelte-i18n';

  import { FormStatuses } from '../constants';

  export let categories,
    companyTypes,
    countries,
    API_BASE_URL,
    API_KEY,
    LANGUAGE;

  const request = createRequest({
    apiBaseUrl: API_BASE_URL,
    apiKey: API_KEY,
    language: LANGUAGE,
  });

  let billingMethods = [];

  // Sweden
  const ORGANISATION_NUMBER_REGEX_1 = /^(\d{1})(\d{5})\-(\d{4})$/;
  // Denmark
  const ORGANISATION_NUMBER_REGEX_2 = /^(\d{1})(\d{7})$/;
  // Norway
  const ORGANISATION_NUMBER_REGEX_3 = /^(\d{1})(\d{8})$/;
  // Finland
  const ORGANISATION_NUMBER_REGEX_4 = /^(\d{1})(\d{6})\-(\d{1})$/;

  const ORGANISATION_NUMBER_MASK = {
    mask: [
      // Sweden: 123456-7890
      { mask: '000000-0000' },
      // Denmark: 12345678
      { mask: '00000000' },
      // Norway: 123456789
      { mask: '000000000' },
      // Finland business id: 1606361-2
      { mask: '0000000-0' },
      // Finland VAT-style with country prefix: FI1606361-2
      { mask: 'aa0000000-0' },
    ],
    prepare: (value) => value.toUpperCase(),
  };

  const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

  const ZIPCODE_SV_MASK = { mask: '000 00' };

  let step = 1;

  const FREE_LICENSE_TYPE_ID = 1;

  let values = {
    OrganisationNumber: '',
    Name: '',
    TypeId: 1,
    LicenseTypeId: FREE_LICENSE_TYPE_ID,
    CategoryName: '',
    SitePath: '',
    Details: '',
    Phone: '',
    BillingInformation: {
      CountryId: COUNTRY_ID,
      VatRegistrationNumber: '',
      // CompanyId: '',
      BillingMethodId: '2', // dropdown
      LicensePlanId: '',
      FirstName: '',
      LastName: '',
      Name: '',
      Street1: '',
      GLN: '',
      // Street2: '',
      ZipCode: '',
      City: '',
      Email: '',
      // PaymentTermsDays: 15 is this needed?
    },
    CompanyUser: {
      FirstName: '',
      LastName: '',
      Phone: '',
      Email: '',
    },
    AcceptUserAgreement: false,
    CountryId: COUNTRY_ID,
  };

  $: countryId = values.BillingInformation.CountryId;

  $: if (countryId) {
    request({
      url: '/billing/methods',
      method: 'GET',
      params: { CountryId: countryId },
    }).then((res) => {
      billingMethods = res.data;
    });
  }

  $: {
    if (
      values.BillingInformation.CountryId === 'SE' &&
      values.OrganisationNumber
    ) {
      values.BillingInformation.VatRegistrationNumber = `${values.BillingInformation.CountryId}${values.OrganisationNumber.replace('-', '')}01`;
    } else if (
      values.BillingInformation.CountryId !== 'SE' &&
      values.OrganisationNumber
    ) {
      values.BillingInformation.VatRegistrationNumber = '';
    }
  }

  $: {
    if (!errors1.SitePath && okSitepath !== values.SitePath) {
      checkSitePathAvailability(values.SitePath);
    }
  }

  let okSitepath;

  $: {
    if (step > 1) {
      const firstInput = document.querySelector('.current');
      if (firstInput) {
        firstInput.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  const checkSitePathAvailability = debounce(async (sitepath) => {
    loading = true;
    try {
      await request({
        url: `/companies/checksitepathvalid`,
        params: { Sitepath: sitepath },
        method: 'GET',
      });
      okSitepath = sitepath;
      errors1.SitePath = '';
      loading = false;
    } catch (error) {
      errors1.SitePath = error.response
        ? error.response.data.Message
        : error.message;
      loading = false;
      okSitepath = '';
    }
  }, 500);

  let touched = {};
  let message, status;
  let loading;
  let emailInUse = false;

  $: errors1 = validate1(values);
  $: errors2 = validate2(values, emailInUse);
  $: errors3 = validate3(values);

  let submissionErrors = {};

  const handleCompanyUserEmailChange = throttle((e) => {
    request({
      url: '/administrators/exists',
      method: 'GET',
      params: { Email: e.target.value.trim() },
    })
      .then((data) => {
        emailInUse = false;
      })
      .catch((error) => {
        if (error.response.data && error.response.data.Exists) {
          emailInUse = true;
        }
      });

    handleCompanyDetailsChange();
  }, 500);

  const handleCompanyDetailsChange = () => {
    values.BillingInformation.FirstName = values.CompanyUser.FirstName;
    values.BillingInformation.LastName = values.CompanyUser.LastName;
    values.BillingInformation.Email = values.CompanyUser.Email.trim();
    values.BillingInformation.Name = values.Name;
  };

  function handleNameChange(e) {
    values.SitePath = slugify(e.target.value, { lower: true });
  }

  function handleSitePathChange(e) {
    values.SitePath = slugify(e.target.value, { lower: true });
  }

  function handleBack(e) {
    e.stopPropagation();
    step -= 1;
  }

  async function handleSubmit(e) {
    if (step < 4) {
      step += 1;

      if (window.dataLayer) {
        // TODO map to correct step
        if (step === 3) {
          dataLayer.push({
            event: 'signup',
            step: 'General Information',
          });
        }
      }
    } else {
      if (!values.AcceptUserAgreement) return;

      values.CompanyUser.Email = values.CompanyUser.Email.trim();
      values.BillingInformation.Email = values.BillingInformation.Email.trim();

      loading = true;

      request({
        url: '/companies',
        method: 'POST',
        data: {
          ...values,
          Email: values.CompanyUser.Email,
          Phone: values.CompanyUser.Phone,
        },
      })
        .then((data) => {
          step += 1;

          if (window.dataLayer) {
            dataLayer.push({
              event: 'signup',
              step: 'Account Creation',
              email: values.CompanyUser.Email,
              phone: values.CompanyUser.Phone,
            });
          }
        })
        .catch((error) => {
          const { data } = error.response;

          if (data && data.ResponseStatus) {
            if (data.ResponseStatus.Errors) {
              const errrors = {};

              data.ResponseStatus.Errors.forEach((error) => {
                submissionErrors = errrors[error.FieldName] = error.Message;
              });

              submissionErrors = errrors;
            }

            if (data.ResponseStatus.Message) {
              message = data.ResponseStatus.Message;
              status = FormStatuses.DANGER;
            }
          }
        })
        .finally(() => {
          loading = false;
        });
    }
  }

  function validate1(values) {
    const errors = {};

    if (!isOrganisationNumberValid(values.OrganisationNumber)) {
      errors.OrganisationNumber = $_(
        'signUp.validations.OrganisationNumber.invalid',
      );
    }
    if (!values.Name) {
      errors.Name = $_('signUp.validations.Name.required');
    }
    if (!values.CategoryName?.trim()) {
      errors.CategoryName = $_('signUp.validations.CategoryId.required');
    }
    if (!values.SitePath) {
      errors.SitePath = $_('signUp.validations.SitePath.required');
    }
    if (!values.Details) {
      errors.Details = $_('signUp.validations.Details.required');
    }

    return errors;
  }
  function validate2(values, emailInUse) {
    const errors = {};

    if (!values.CompanyUser.FirstName) {
      errors.CompanyUserFirstName = $_(
        'signUp.validations.CompanyUser.FirstName.required',
      );
    }
    if (!values.CompanyUser.LastName) {
      errors.CompanyUserLastName = $_(
        'signUp.validations.CompanyUser.LastName.required',
      );
    }

    const email = values.CompanyUser.Email.trim();

    if (!email) {
      errors.CompanyUserEmail = $_(
        'signUp.validations.CompanyUser.Email.required',
      );
    } else if (!EMAIL_REGEX.test(email)) {
      errors.CompanyUserEmail = $_(
        'signUp.validations.CompanyUser.Email.invalid',
      );
    } else if (emailInUse) {
      errors.CompanyUserEmail = $_(
        'signUp.validations.CompanyUser.Email.emailInUse',
      );
    }

    return errors;
  }

  function validate3(values) {
    const errors = {};

    if (!values.BillingInformation.Name) {
      errors.BillingInformationName = $_(
        'signUp.validations.BillingInformation.Name.required',
      );
    }

    if (!values.BillingInformation.Street1) {
      errors.BillingInformationStreet1 = $_(
        'signUp.validations.BillingInformation.Street1.required',
      );
    }

    if (!values.BillingInformation.City) {
      errors.BillingInformationCity = $_(
        'signUp.validations.BillingInformation.City.required',
      );
    }

    if (!values.BillingInformation.ZipCode) {
      errors.BillingInformationZipCode = $_(
        'signUp.validations.BillingInformation.ZipCode.required',
      );
    }

    return errors;
  }

  function isOrganisationNumberValid(value) {
    const normalised = typeof value === 'string' ? value.trim() : '';
    const finnishValue = normalised.replace(/^FI/i, '');

    return (
      // Sweden
      ORGANISATION_NUMBER_REGEX_1.test(normalised) ||
      // Denmark
      ORGANISATION_NUMBER_REGEX_2.test(normalised) ||
      // Norway
      ORGANISATION_NUMBER_REGEX_3.test(normalised) ||
      // Finland (accept both 1606361-2 and FI1606361-2)
      FinnishBusinessIds.isValidBusinessId(finnishValue)
    );
  }

  function getOrganisaitonNumberPlaceholder(countryId) {
    switch (countryId) {
      case 'SE':
        return ORGANISATION_NUMBER_MASK.mask[0].mask.replaceAll('0', 'x');
      case 'DK':
        return ORGANISATION_NUMBER_MASK.mask[1].mask.replaceAll('0', 'x');
      case 'NO':
        return ORGANISATION_NUMBER_MASK.mask[2].mask.replaceAll('0', 'x');
      case 'FI':
        return ORGANISATION_NUMBER_MASK.mask[3].mask.replaceAll('0', 'x');
      default:
        return ORGANISATION_NUMBER_MASK.mask[0].mask.replaceAll('0', 'x');
    }
  }

  let organisationNumberPlaceholder =
    getOrganisaitonNumberPlaceholder(countryId);
  $: {
    organisationNumberPlaceholder = getOrganisaitonNumberPlaceholder(countryId);
  }

  function handleBlur(e) {
    touched = { ...touched, [e.target.name]: true };
    if (e.target.name === 'Name') {
      touched['SitePath'] = true;
    }

    message = '';
  }
</script>

<svelte:head>
  <title>{$_('signUp.title')} | {$_('common.title')}</title>
</svelte:head>

<div class="progress">
  <div class="progress__pretitle">
    {$_('signUp.youAreNSimpleStepsAwayTo', { values: { number: 5 - step } })}
  </div>
  <h1 class="progress__title">{$_('signUp.connectToBokaMera')}</h1>
  <div class="progress__subtitle">{$_('signUp.itsTotallyFreeToRegister')}</div>
  <div class="progress__stepper">
    <Stepper
      disabled={loading || step === 5}
      onStepClick={(clickedStep) => {
        if (clickedStep < step) {
          step = clickedStep;
        }
      }}
      steps={[
        $_('signUp.generalInformation.heading'),
        $_('signUp.account'),
        $_('signUp.selectPricePlan.heading'),
        $_('signUp.billingInformation'),
        $_('signUp.youAreDone'),
      ]}
      currentStep={step - 1}
    />
  </div>
</div>

<div class="sign-up container">
  <form class="sign-up__form" on:submit|preventDefault={handleSubmit}>
    {#if step === 1}
      <Field
        bind:value={values.Name}
        error={touched.Name && errors1.Name}
        id="Name"
        onBlur={handleBlur}
        label={$_('signUp.Name')}
        onChange={handleNameChange}
      />
      <Field
        bind:value={values.BillingInformation.CountryId}
        id="BillingInformationCountryId"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.CountryId')}
        type="select"
      >
        {#each countries as country}
          <option value={country.Id}>{country.Name}</option>
        {/each}
      </Field>
      <Field
        bind:value={values.CategoryName}
        error={touched.CategoryName && errors1.CategoryName}
        id="CategoryName"
        onBlur={handleBlur}
        label={$_('signUp.CategoryId')}
        type="combo"
        options={categories.map((category) => ({
          value: category.Name,
          text: category.Name,
        }))}
      />
      <Field
        bind:value={values.OrganisationNumber}
        mask={ORGANISATION_NUMBER_MASK}
        error={touched.OrganisationNumber && errors1.OrganisationNumber}
        id="OrganisationNumber"
        onBlur={handleBlur}
        onChange={(evt) => {
          if (
            values.BillingInformation.CountryId === 'SE' &&
            evt.target.value.length === 6
          ) {
            values.OrganisationNumber = `${evt.target.value}-`;
            setTimeout(() => {
              evt.target.focus();
              evt.target.setSelectionRange(7, 7);
            }, 100);
          }
        }}
        label={$_('signUp.OrganisationNumber')}
        placeholder={organisationNumberPlaceholder}
      />
      <Field
        value={values.SitePath}
        error={touched.SitePath && errors1.SitePath}
        id="SitePath"
        onBlur={handleBlur}
        label={$_('signUp.SitePath')}
        prefix="https://"
        suffix=".{ORIGIN}"
        onChange={handleSitePathChange}
      />
      <Field
        bind:value={values.Details}
        error={touched.Details && errors1.Details}
        id="Details"
        onBlur={handleBlur}
        label={$_('signUp.Details')}
        type="textarea"
      />
      <div class="field">
        <div class="col-9 form__buttons">
          <Button
            primary
            type="submit"
            disabled={Object.keys(errors1).filter((key) => errors1[key])
              .length > 0}
          >
            {$_('common.continue')}
          </Button>
        </div>
      </div>
    {/if}
    {#if step === 2}
      <Field
        bind:value={values.CompanyUser.FirstName}
        error={touched.CompanyUserFirstName && errors2.CompanyUserFirstName}
        id="CompanyUserFirstName"
        onBlur={handleBlur}
        onChange={handleCompanyDetailsChange}
        label={$_('signUp.CompanyUser.FirstName')}
      />
      <Field
        bind:value={values.CompanyUser.LastName}
        error={touched.CompanyUserLastName && errors2.CompanyUserLastName}
        id="CompanyUserLastName"
        onBlur={handleBlur}
        onChange={handleCompanyDetailsChange}
        label={$_('signUp.CompanyUser.LastName')}
      />
      <Field
        bind:value={values.CompanyUser.Phone}
        error={touched.CompanyUserPhone && errors2.CompanyUserPhone}
        id="CompanyUserPhone"
        onBlur={handleBlur}
        onChange={handleCompanyDetailsChange}
        label={$_('signUp.CompanyUser.Phone')}
      />
      <Field
        bind:value={values.CompanyUser.Email}
        error={touched.CompanyUserEmail && errors2.CompanyUserEmail}
        id="CompanyUserEmail"
        onBlur={(e) => {
          values.CompanyUser.Email = values.CompanyUser.Email.trim();
          handleBlur(e);
        }}
        label={$_('signUp.CompanyUser.Email')}
        onChange={handleCompanyUserEmailChange}
      />
      <Field
        bind:checked={values.AcceptUserAgreement}
        id="AcceptUserAgreement"
        onBlur={handleBlur}
        label={$_('signUp.AcceptUserAgreement')}
        type="checkbox"
      />
      <div class="container">
        <div class="grid justify-center">
          <div class="col-6">
            <div class="muted mb-2">
              <p>
                Genom att godkänna Användarvillkoren ovan ingår du ett juridiskt
                bindande avtal med Spiris.
              </p>
              <p>
                När du, som representant för ett företag eller motsvarande,
                anger dina uppgifter ovan kommer Spiris även att använda dessa
                uppgifter för att skicka ut information och nyheter som vi tror
                kan vara av intresse för dig. Du kan när som helst avregistrera
                dig från våra utskick genom att trycka på avregistrera-knappen i
                våra mail, eller genom att kontakta oss.
              </p>
              <p>
                Dina personuppgifter behandlas i enlighet med BokaMeras <a
                  href="https://bokamera.se/privacy">integritetspolicy</a
                >.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="field">
        <div class="col-9 form__buttons">
          <Button disabled={loading} secondary on:click={handleBack}
            >{$_('common.back')}</Button
          >
          <Button
            primary
            type="submit"
            disabled={Object.keys(errors2).length > 0 ||
              loading ||
              !values.AcceptUserAgreement}
            {loading}
          >
            {$_('common.continue')}
          </Button>
        </div>
      </div>
    {/if}
    {#if step === 3}
      <SelectPricePlans
        {API_BASE_URL}
        {API_KEY}
        {LANGUAGE}
        countryId={values.BillingInformation.CountryId}
        onSelect={(licenseId, pricePlanId) => {
          values.BillingInformation.LicensePlanId = pricePlanId;
          values.LicenseTypeId = licenseId;
          step++;
        }}
      />
    {/if}
    {#if step === 4}
      {#if values.LicenseTypeId === FREE_LICENSE_TYPE_ID}
        <div class="info-bar">
          {@html $_('signUp.billingInfoNotice')}
        </div>
      {/if}
      <Field
        bind:value={values.BillingInformation.BillingMethodId}
        id="BillingInformationBillingMethodId"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.BillingMethodId')}
        type="select"
      >
        {#each billingMethods as billingMethod}
          <option value={billingMethod.Id.toString()}
            >{billingMethod.Name}</option
          >
        {/each}
      </Field>

      {#if values.BillingInformation.BillingMethodId !== '1' && values.BillingInformation.BillingMethodId !== '2'}
        <Field
          bind:value={values.BillingInformation.GLN}
          error={touched.BillingInformationGLN && errors3.GLN}
          id="GLN"
          onBlur={handleBlur}
          label={$_('signUp.BillingInformation.GLN')}
        />
      {/if}

      <Field
        bind:value={values.BillingInformation.Name}
        error={touched.BillingInformationCompanyName &&
          errors3.BillingInformationName}
        id="BillingInformationCompanyName"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.Name')}
      />

      <Field
        bind:value={values.BillingInformation.FirstName}
        error={touched.BillingInformationFirstName &&
          errors3.BillingInformationFirstName}
        id="BillingInformationFirstName"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.FirstName')}
      />

      <Field
        bind:value={values.BillingInformation.LastName}
        error={touched.BillingInformationLastName &&
          errors3.BillingInformationLastName}
        id="BillingInformationLastName"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.LastName')}
      />

      <Field
        bind:value={values.BillingInformation.Email}
        error={touched.BillingInformationEmail &&
          errors3.BillingInformationEmail}
        id="BillingInformationEmail"
        onBlur={(e) => {
          values.BillingInformation.Email =
            values.BillingInformation.Email.trim();
          handleBlur(e);
        }}
        label={$_('signUp.BillingInformation.Email')}
      />

      <Field
        bind:value={values.BillingInformation.Street1}
        error={touched.BillingInformationStreet1 &&
          errors3.BillingInformationStreet1}
        id="BillingInformationStreet1"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.Street1')}
      />

      <Field
        bind:value={values.BillingInformation.ZipCode}
        error={touched.BillingInformationZipCode &&
          errors3.BillingInformationZipCode}
        id="BillingInformationZipCode"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.Zipcode')}
      />

      <Field
        bind:value={values.BillingInformation.City}
        error={touched.BillingInformationCity && errors3.BillingInformationCity}
        id="BillingInformationCity"
        autocomplete="City"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.City')}
      />

      <Field
        bind:value={values.BillingInformation.CountryId}
        id="BillingInformationCountryId"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.CountryId')}
        type="select"
      >
        {#each countries as country}
          <option value={country.Id}>{country.Name}</option>
        {/each}
      </Field>

      <Field
        bind:value={values.BillingInformation.VatRegistrationNumber}
        id="BillingInformationVatRegistrationNumber"
        onBlur={handleBlur}
        label={$_('signUp.BillingInformation.VatRegistrationNumber')}
      />
      <div class="field">
        <div class="col-9 form__buttons">
          <Button disabled={loading} secondary on:click={handleBack}
            >{$_('common.back')}</Button
          >
          <Button
            primary
            type="submit"
            disabled={Object.keys(errors3).length > 0 || loading}
            {loading}
          >
            {$_('common.complete')}
          </Button>
        </div>
      </div>
      {#if message}
        <div class="field grid">
          <div class="col-3" />
          <div class="col-6">
            <div class="alert-container">
              <Alert {message} type={status} />
            </div>
          </div>
        </div>
      {/if}
    {/if}
    {#if step === 5}
      <div class="done">
        <h3>{$_('signUp.congratulations')}</h3>
        <div class="subheader">
          <i class="fas fa-check" />
          {$_('signUp.youMustVerify1')}
          (
          <a href={`mailto:${values.CompanyUser.Email}`}>
            {values.CompanyUser.Email}
          </a>
          ).
          {$_('signUp.youMustVerify2')}
          <a href="mailto:support@bokamera.se">support@bokamera.se</a>
        </div>
        <div class="info-bar">
          {$_('signUp.vismaOnlineEmailNotice')}
        </div>
      </div>
    {/if}
  </form>
</div>

<style>
  .progress {
    padding: 50px 14px;
    text-align: center;
  }
  .progress__pretitle {
    color: #888;
    text-transform: uppercase;
    font-size: 16px;
    margin-bottom: 20px;
  }
  .progress__title {
    font-size: 28px;
    margin: 0 0 20px;
  }
  .progress__subtitle {
    color: #888;
    text-transform: uppercase;
    font-size: 16px;
    margin-bottom: 50px;
  }
  .progress__stepper {
    display: flex;
    justify-content: center;
  }

  .field {
    display: flex;
    align-items: baseline;
    justify-content: flex-start;
    margin-bottom: 30px;
  }

  .form__buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .form__buttons :global(.button) {
    display: block;
    margin-left: 20px;
  }

  .sign-up {
    padding: 0 14px 70px;
  }
  .sign-up__form {
    max-width: 1000;
    margin: 0 auto;
  }

  h3 {
    margin-bottom: 20px;
  }

  .done {
    max-width: 800px;
    margin: auto;
  }

  @media (max-width: 800px) {
    .field {
      display: block;
    }
  }

  .loading.center {
    text-align: center;
  }
  .muted {
    color: #888;
    font-size: smaller;
  }
  .justify-center {
    justify-content: center;
  }

  .p-1 {
    padding: 10px;
  }

  .p-2 {
    padding: 20px;
  }

  .mb-1 {
    margin-bottom: 10px;
  }
  .mb-2 {
    margin-bottom: 20px;
  }

  .info-bar {
    background-color: #f5f5f5;
    border-left: 4px solid #6a35a3;
    padding: 12px 16px;
    margin-bottom: 24px;
    font-size: 14px;
    line-height: 1.4;
  }
  .done .info-bar {
    margin-top: 24px;
  }
</style>
